/**
 * A Connect partner link opens this app at `/login?token=<jwt>`. The token is a
 * bearer credential, so it leaves the address bar before any script can copy the
 * URL: `src/scripts/connect-token-strip.js`, the first script in index.html,
 * strips it and parks it in `window.__viveConnectToken`, and the login page
 * trades it for a session cookie. It waits in that one-shot slot in between.
 */

declare global {
  interface Window {
    __viveConnectToken?: string;
  }
}

/**
 * The paths that render `LoginComponent` (app-routing.module.ts); password reset
 * is not one. connect-token-strip.js repeats this rule.
 */
function rendersLoginComponent(pathname: string): boolean {
  if (pathname === '/' || pathname === '/login') {
    return true;
  }
  return (
    pathname.startsWith('/login/') &&
    !pathname.startsWith('/login/forgot-password')
  );
}

/**
 * main.ts's fallback for the head script, so a blocked or failed script still
 * keeps the token out of Datadog RUM and PostHog. Once the script has run the
 * token is already gone from the URL and this does nothing.
 */
export function stripConnectTokenFromUrl(
  location: Pick<Location, 'pathname' | 'search' | 'hash'>,
  history: Pick<History, 'state' | 'replaceState'>
): void {
  if (!rendersLoginComponent(location.pathname)) {
    return;
  }
  const params = new URLSearchParams(location.search);
  const token = params.get('token');
  if (!token) {
    return;
  }
  params.delete('token');
  const query = params.toString();
  history.replaceState(
    history.state,
    '',
    `${location.pathname}${query ? `?${query}` : ''}${location.hash}`
  );
  window.__viveConnectToken = token;
}

/** True while a parked token waits for the login page; the vendor redirect stands aside so it is not lost. */
export function hasPendingConnectToken(): boolean {
  return typeof window.__viveConnectToken === 'string';
}

/** The parked token, at most once: it is cleared on read, so a later visit to the login page must not replay it. */
export function takeConnectToken(): string | null {
  const token = window.__viveConnectToken ?? null;
  delete window.__viveConnectToken;
  return token;
}
